import { useEffect, useMemo, useRef, useState } from 'react';

import {
  type CommunicationDocument,
  type CommunicationItem,
  type CommunicationType,
  formatCommunicationDate,
  getCommunicationDocuments,
  getCommunicationPeople,
  getTypeMeta,
} from './useCommunicationCard';

export type CommunicationPreviewProps = {
  item: CommunicationItem;
  documents?: CommunicationDocument[];
  maxLines?: number;
  'data-testid'?: string;
};

export const DEFAULT_PREVIEW_LINES = 2;

// Tags emitted by the rich-text editor. A plain-text e-mail body can hold `<jean@exemple.fr>`
// or `<https://…>`, which must stay text rather than be parsed as markup.
const HTML_TAG_PATTERN = /<\/?(p|br|div|span|ul|ol|li|strong|em|b|i|u|a|h[1-6]|blockquote)\b[^>]*>/i;

const HTML_ENTITIES: Record<string, string> = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
};

export function isHtmlContent(content: string): boolean {
  return HTML_TAG_PATTERN.test(content);
}

/**
 * Flattens a comment into a single run of text for the clamped preview,
 * the way an inbox row previews a message.
 */
export function toCommentPreview(content: string): string {
  const text = isHtmlContent(content)
    ? content
        .replace(/<br\s*\/?>|<\/(p|div|li|h[1-6]|blockquote)>/gi, ' ')
        .replace(/<[^>]+>/g, '')
        .replace(/&(nbsp|amp|lt|gt|quot|#39);/g, (entity) => HTML_ENTITIES[entity])
    : content;

  return text.replace(/\s+/g, ' ').trim();
}

export function getFullCommentLabel(type: CommunicationType): string {
  return type === 'email' ? 'Voir le mail' : 'Voir en entier';
}

export function useCommunicationPreview(props: CommunicationPreviewProps) {
  const typeMeta = getTypeMeta(props.item.type);
  const previewRef = useRef<HTMLParagraphElement>(null);
  const [isPreviewTruncated, setIsPreviewTruncated] = useState(false);
  const [isFullCommentOpen, setIsFullCommentOpen] = useState(false);

  const comment = props.item.comment?.trim() ?? '';
  const commentPreview = useMemo(() => toCommentPreview(comment), [comment]);
  const maxLines = props.maxLines ?? DEFAULT_PREVIEW_LINES;

  const rawDate = props.item.date;
  const formattedDate = useMemo(() => formatCommunicationDate(rawDate), [rawDate]);
  const people = getCommunicationPeople(props.item);

  const documents = useMemo(
    () => getCommunicationDocuments(props.documents ?? props.item.documents),
    [props.documents, props.item.documents],
  );

  // The clamp hides the overflow in CSS: measure it to offer the full view only when text is cut.
  useEffect(() => {
    const element = previewRef.current;
    if (!element) {
      setIsPreviewTruncated(false);
      return undefined;
    }

    function measure() {
      if (element) {
        setIsPreviewTruncated(element.scrollHeight > element.clientHeight + 1);
      }
    }

    measure();

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [commentPreview, maxLines]);

  function handleOpenFullComment() {
    setIsFullCommentOpen(true);
  }

  function handleCloseFullComment() {
    setIsFullCommentOpen(false);
  }

  return {
    comment,
    commentPreview,
    hasComment: commentPreview.length > 0,
    isHtmlComment: isHtmlContent(comment),
    maxLines,
    previewRef,
    isPreviewTruncated,
    isFullCommentOpen,
    fullCommentLabel: getFullCommentLabel(props.item.type),
    dialogTitle: props.item.title?.trim() || typeMeta.label,
    typeLabel: typeMeta.label,
    tagColor: typeMeta.tagColor,
    IconComponent: typeMeta.icon,
    isExchange: typeMeta.isExchange,
    formattedDate,
    providerName: people.providerName,
    contactName: people.contactName,
    documents,
    handleOpenFullComment,
    handleCloseFullComment,
  };
}
